import {
  convertImageUrlToBase64,
  decodeFromBase64,
  getContentType,
  getContentTypeCategory,
  isMockResponseEncoded,
} from '@carboncopyco/editor-utils';
import type {
  CustomModifier,
  MockResponseModifier,
  Modification,
  TransactionModifier,
  TransactionRule,
} from '@carboncopyco/types';
import { transformToKebabCase } from '@carboncopyco/utils';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { mixed, object } from 'yup';
import type { TransactionModifiersForm } from '../CreateTransactionRuleModal';
import type { JsonFieldSchema } from '../JsonForm';
import {
  type DynamicResponseModifier,
  contentReplaceModifiersSchema,
  headersSchema,
  modifiersSchema,
  responseSchema,
  responseTypeSchema,
  ruleDescriptionSchema,
  ruleNameSchema,
  schemaResolver,
  selectorsSchema,
} from '../TransactionRuleForm';

const getMockResponseModifier = (modifiers: TransactionModifier[]) => {
  const modifier = modifiers.find(
    (modifier): modifier is MockResponseModifier =>
      modifier.type === 'MockResponse'
  );
  return modifier
    ? {
        ...modifier,
        arguments: {
          ...modifier.arguments,
          headers: transformToKebabCase(modifier.arguments.headers),
        },
      }
    : undefined;
};

export interface FormInitialValues {
  transactionRule: TransactionRule;
  transactionModifications: Modification<'transaction_rule'>[];
}
export const useEditTransactionRuleForm = ({
  transactionRule,
  transactionModifications,
}: FormInitialValues) => {
  const mockResponseModifier = useMemo(
    () => getMockResponseModifier(transactionRule.rules[0].modifiers),
    [transactionRule]
  );

  const isCustomModifier = useMemo(
    () =>
      transactionRule.rules[0].modifiers.some(
        (modifier: CustomModifier) => modifier.arguments.isCustomModifier
      ),
    [transactionRule]
  );

  const schema = useMemo(() => {
    return object().shape({
      name: ruleNameSchema(
        transactionModifications.filter(
          (rule) => rule.content.name !== transactionRule.name
        )
      ),
      description: ruleDescriptionSchema,
      selectors: selectorsSchema.optional(),
      responseType: responseTypeSchema,
      response: responseSchema,
      dynamicResponseModifiers: contentReplaceModifiersSchema,
      headers: headersSchema,
      customModifier: isCustomModifier ? modifiersSchema.optional() : mixed(),
    });
  }, [transactionModifications, transactionRule, isCustomModifier]);

  return useForm<TransactionModifiersForm>({
    defaultValues: async () => {
      const commonValues = {
        dynamicResponseModifiers: [],
        name: transactionRule.name,
        description: transactionRule.description ?? '',
        selectors: JSON.stringify(transactionRule.rules[0].selectors),
      };

      if (mockResponseModifier) {
        const response = await getMockResponse(mockResponseModifier);
        const representationFields =
          await getRepresentationFields(mockResponseModifier);

        return {
          ...commonValues,
          response,
          responseType: isCustomModifier ? 'custom' : 'static',
          headers: mockResponseModifier.arguments.headers,
          customModifier:
            isCustomModifier &&
            JSON.stringify(transactionRule.rules[0].modifiers),
          representationType:
            mockResponseModifier.arguments.representationType ?? 'raw',
          representationFields,
        };
      }

      if (isCustomModifier) {
        return {
          ...commonValues,
          response: '',
          customModifier: JSON.stringify(transactionRule.rules[0].modifiers),
          responseType: 'custom',
        };
      }

      return {
        ...commonValues,
        response: '',
        dynamicResponseModifiers: transactionRule.rules[0].modifiers.map(
          (modifier) => ({
            id: uuid(),
            ...modifier,
          })
        ) as DynamicResponseModifier[],
        responseType: 'dynamic',
      };
    },
    resolver: schemaResolver(schema) as any,
  });
};

const getMockResponse = async (modifier: MockResponseModifier) => {
  const contentTypeCategory = getContentTypeCategory(
    getContentType(modifier.arguments.headers ?? [])
  );

  if (isMockResponseEncoded(modifier, 'url')) {
    if (contentTypeCategory === 'image') {
      const base64 = await convertImageUrlToBase64(modifier.arguments.content);
      return (base64.split(';base64,') ?? [])[1];
    }
    return convertTextUrlToBase64(modifier.arguments.content);
  }

  const response = modifier?.arguments.content ?? '';
  return contentTypeCategory === 'image'
    ? response
    : decodeFromBase64(response);
};

const convertTextUrlToBase64 = async (url: string) => {
  try {
    const response = await fetch(url);
    const text = await response.text();
    return text;
  } catch (_error) {
    return '';
  }
};

const getRepresentationFields = async (
  modifier: MockResponseModifier
): Promise<JsonFieldSchema[]> => {
  const { representationFields, representationFieldsUrl } = modifier.arguments;

  if (representationFields) {
    return representationFields;
  }

  if (!representationFieldsUrl) {
    return [];
  }

  try {
    const response = await fetch(representationFieldsUrl);
    const fields = await response.json();
    return fields;
  } catch (_error) {
    return [];
  }
};
